import { DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ComplianceAssociatedCourseModel } from 'src/app/models/compliance/compliance-associated-course.model';
import { ComplianceRenewalFrequencyModel } from 'src/app/models/compliance/compliance-renewal-frequency.model';
import { TableColumn } from 'src/app/models/table-column.model';

@Component({
  selector: 'app-compliance-associated-courses',
  standalone: false,
  templateUrl: './compliance-associated-courses.component.html',
  styleUrl: './compliance-associated-courses.component.scss',
})
export class ComplianceAssociatedCoursesComponent {
  @Input()
  complianceId!: string;

  @Input()
  renewalFrequency!: ComplianceRenewalFrequencyModel;

  @Input()
  associatedCourses: ComplianceAssociatedCourseModel[] = [];

  @Output() createCourse = new EventEmitter<void>();
  
  @Output() courseClick = new EventEmitter<string>();

  associatedCourseColumns: TableColumn[] = [
    {
      caption: '',
      dataField: 'imageUrl',
      cellTemplate: 'image', 
      disableFiltering: true,
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.NAME'),
      dataField: 'description',
      cellTemplate: 'titleDescription',
      disableFiltering: true,
      calculateDisplayValue: (rowData: ComplianceAssociatedCourseModel): any => {
        return {
          title: rowData.name ?? '',
          description: rowData.description
        };
      },
      filterExpression: (value: string) => {
        function getTitleDescriptionFilterString(rowData: ComplianceAssociatedCourseModel) {
          const text = `${rowData.name ?? ''} ${rowData.description ?? ''}`;
          return text.trim() || 'empty';
        }

        return [
          [
            getTitleDescriptionFilterString, 
            'contains', 
            value?.trim() || 'empty'
          ]
        ];
      },
      cellValue: (rowData: ComplianceAssociatedCourseModel): string => {
        return `${rowData.name} ${rowData.description}`
      },
    },
    {
      caption: this.translateService.instant('COMPLIANCE.FIELDS.COMPLIANCE-HIT-RATE'),
      dataField: 'complianceHitRatePercentage',
      calculateDisplayValue: (rowData: ComplianceAssociatedCourseModel): string => {
        return rowData.complianceHitRatePercentage + ' %';
      },
      disableFiltering: true,
    },
    {
      caption: this.translateService.instant('COMMON.FIELDS.DATE'),
      dataField: 'date',
      calculateDisplayValue: (rowData: ComplianceAssociatedCourseModel) => {
        return this.displayCourseDate(rowData);
      },
      disableFiltering: true,
    },
    {
      caption: '',
      dataField: 'this',
      cellType: 'action',
      disableFiltering: true,
      cellTemplate: 'actionsTemplate',
      actions: [
        {
          text: this.translateService.instant('COMPLIANCE.COMMON.GO-TO-COURSE'),
          isButton: true,
          buttonType: 'default',
          isShowAction: () => true,
          onClick: (data: ComplianceAssociatedCourseModel) => {
            this.courseClick.emit(data.id);
          },
        },
      ],
    },
  ];

  constructor(
    private translateService: TranslateService,
  ) { }

  ngOnInit(): void {
    if(this.renewalFrequency === undefined) return;
    this.configureDateColumn();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['renewalFrequency']) {
      if(this.renewalFrequency === undefined) return;
      this.configureDateColumn();
    }
  }
  
  private configureDateColumn(): void {
    const dateColumn = this.associatedCourseColumns.find(c => c.dataField === 'date');
    if (!dateColumn) return;

    switch (this.renewalFrequency) {
      case ComplianceRenewalFrequencyModel.Never:
        dateColumn.visible = false;
        break;

      case ComplianceRenewalFrequencyModel.ThreeMonths:
      case ComplianceRenewalFrequencyModel.SixMonths:
        dateColumn.caption = this.translateService.instant('COMMON.FIELDS.PERIOD');
        dateColumn.visible = true;
        break;

      case ComplianceRenewalFrequencyModel.OneYear:
      case ComplianceRenewalFrequencyModel.TwoYears:
      case ComplianceRenewalFrequencyModel.ThreeYears:
        dateColumn.caption = this.translateService.instant('COMMON.FIELDS.YEAR');
        dateColumn.visible = true;
        break;

      default:
        console.error('Unknown renewal frequency:', this.renewalFrequency);
        throw new Error('Invalid renewal frequency selected.');
    }
  }

  displayCourseDate(rowData: ComplianceAssociatedCourseModel) {
    switch (this.renewalFrequency) {
      case ComplianceRenewalFrequencyModel.Never: 
        return '';

      case ComplianceRenewalFrequencyModel.ThreeMonths:
      case ComplianceRenewalFrequencyModel.SixMonths: {
        const datePipe = new DatePipe(this.translateService.currentLang);

        const startDateStr = datePipe.transform(rowData.date.start, 'dd MMMM yyyy');
        const endDateStr = datePipe.transform(rowData.date.end, 'dd MMMM yyyy');
        return startDateStr + ' - ' + endDateStr;
      }

      case ComplianceRenewalFrequencyModel.OneYear:
      case ComplianceRenewalFrequencyModel.TwoYears:
      case ComplianceRenewalFrequencyModel.ThreeYears: {
        return new Date(rowData.date.start).getFullYear();
      }

      default:  
        console.error('Unknown renewal frequency:', this.renewalFrequency);
        throw new Error('Invalid renewal frequency selected.');
    }
  }

  newComplianceCourseHandler(): void {
    this.createCourse.emit();
  }

  complianceCourseNavigateHandler(id?: string) {
    this.courseClick.emit(id);
  }
}
